import { Button, Col, Divider, Row, Select } from "antd";
import { useContext, useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./style.module.scss";
import BannerTrademarkContext from "../../../context/banner-trademark/context";
import CategoryContext from "../../../context/category/context";
import { IProduct } from "../../../models/product/response";
import { IStore } from "../../../models/store/response";
import AppContext from "../../../context/app/context";
import { IUpdateBanner } from "../../../models/banner/request";
import EndPoints from "../../../services/end-points";
import { getTranslations } from "../../../utils/helpers/translations";
import Spin from "../../general/antd/spin";
import { languages } from "../../../utils/helpers/constants";
import FieldBuilder from "../../form-components/field-builder";
import FormItem from "../../general/form-item";
import { getBannerType } from "../../../models/banner/enum";
import { getUserStatus } from "../../../models/user/enum";
import BannerTrademarkContextProvider from "../../../context/banner-trademark/provider";
import { execute } from "../../../utils/helpers/execute";
import { ITrademarkLight } from "../../../models/trademark-manager/response";
import { ICategory } from "../../../models/category/response";
import { IMedium } from "../../../models/medium/response";
import { MediumFor } from "../../../models/medium/enum";
import { errorNotification, successNotification } from "../../../utils/helpers/notification";
import ImageFieldBlock from "../../general/image-field-block";
import { appendImageProcessingFields, getImageProcessingMode, hasPendingImageUpload, resolveUploadFile } from "../../../utils/helpers/media-upload";
import { createActiveStoreFilter } from "../../../utils/helpers/store-filters";

const Form = () => {
  const { details, actions, loading } = useContext(BannerTrademarkContext);
  const { allCategories, actions: { getAllData } } = useContext(CategoryContext);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState<boolean>(true);
  const [stores, setStores] = useState<IStore[]>([]);
  const [storeLoading, setStoreLoading] = useState(true);
  const { direction } = useContext(AppContext);
  const Option = Select.Option;
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [trademarks, setTrademarks] = useState<ITrademarkLight[]>([]);
  const [trademarksLoading, setTrademarksLoading] = useState<boolean>(false);
  const banners = [1, 2, 3, 4, 5, 6, 7, 8];
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [selectedEntityType, setSelectedEntityType] = useState<'store' | 'trademark' | null>(null);

  const [newImage, setNewImage] = useState<string | null>(null);
  const [newImageFile, setNewImageFile] = useState<File | undefined>();
  const [originalImageFile, setOriginalImageFile] = useState<File | undefined>();
  const [imageChanged, setImageChanged] = useState<boolean>(false);
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [withoutBackground, setWithoutBackground] = useState<boolean>(false);
  const [preserveOriginal, setPreserveOriginal] = useState<boolean>(false);

  const methods = useForm<any>({
    defaultValues: {
      imageId: details?.image?.id ?? undefined,
      rateableId: details?.rateableId ?? undefined,
      link: details?.link ?? undefined,
      for: 1,
      trademarkId: details?.trademark?.id,
      categoryId: details?.category?.id
    } as IUpdateBanner,
  });
  const { handleSubmit, reset, control, setValue, trigger } = methods;

  const rateableType = useWatch({
    control,
    name: "rateableType",
  });

  const selectedEntityId = useWatch({
    control,
    name: "rateableId"
  });

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (
      scrollHeight - scrollTop === clientHeight &&
      hasMore &&
      !productsLoading
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  useEffect(() => {
    const getProducts = async () => {
      try {
        const { data } = await EndPoints.product.getAllProducts({
          light: true,
          page: page,
          perPage: 250,
        });
        const newOptions = data?.data;
        setProducts((prevOptions) => [...prevOptions, ...newOptions]);
        setHasMore(data.lastPage == page ? false : true);
      } catch (error) {
      } finally {
        setProductsLoading(false);
      }
    };
    getProducts();
  }, [direction, page]);

  useEffect(() => {
    const getTradmarks = async () => {
      try {
        const { data } = await EndPoints.trademark.getAllTrademark({
          light: true,
          perPage: -1,
        });
        setTrademarks(data.data);
        setTrademarksLoading(false);
      } catch (error) {
      } finally {
        setTrademarksLoading(false);
      }
    };
    getTradmarks();
    !allCategories && getAllData();
  }, [direction]);

  useEffect(() => {
    const getStores = async () => {
      try {
        const { data } = await EndPoints.store.getAllStores({
          light: true,
          perPage: -1,
          filters: [createActiveStoreFilter()],
        });
        setStores(data.data);
      } catch (error) {
      } finally {
        setStoreLoading(false);
      }
    };
    getStores();
  }, [direction]);

  const handleImageChange = (response: IMedium | null) => {
    setNewImage(response?.url ?? null);
    setNewImageFile(response?.file);
    setOriginalImageFile(response?.originalFile);
    setImageChanged(Boolean(response));
    setWithoutBackground(response ? false : Boolean(details?.image?.without_background));
    setPreserveOriginal(false);
  };

  const handleUploadNewImage = async () => {
    if (!newImage) {
      errorNotification(t("no_new_image_selected"));
      return;
    }
    try {
      setImageLoading(true);
      const timestamp = new Date().getTime();
      const file = await resolveUploadFile({
        imageUrl: newImage,
        processedFile: newImageFile,
        originalFile: originalImageFile,
        preserveOriginal,
        fallbackName: `banner-image-${timestamp}`,
      });
      const formData = new FormData();
      formData.append("medium", file);
      formData.append("type", "1");
      formData.append("for", MediumFor.UserProfile);
      appendImageProcessingFields(
        formData,
        getImageProcessingMode(withoutBackground, preserveOriginal),
      );

      const uploadRes = await EndPoints.media.uploadMedia(formData);
      const uploadedId = uploadRes?.id;
      if (!uploadedId) {
        throw new Error(t("invalid_upload_response_no_id_returned"));
      }

      setValue("imageId", uploadedId, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });
      await trigger("imageId");

      setNewImage(uploadRes?.url ?? newImage);
      setNewImageFile(undefined);
      setOriginalImageFile(undefined);
      setPreserveOriginal(false);
      setImageChanged(false);
      successNotification(t("image_uploaded_successfully"));
    } catch (err: any) {
      console.error("Upload failed:", err);
      let errorMessage = t("failed_to_upload_image");
      if (err.message?.includes("Failed to fetch")) {
        errorMessage = t("failed_to_load_image");
      } else if (err.message?.includes("Invalid upload response")) {
        errorMessage = t("server_error_try_again");
      }
      errorNotification(errorMessage);
    } finally {
      setImageLoading(false);
    }
  };

  const handleDeleteImage = async () => {
    try {
      setImageLoading(true);
      setValue("imageId", null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });
      await trigger("imageId");

      setNewImage(null);
      setNewImageFile(undefined);
      setOriginalImageFile(undefined);
      setImageChanged(false);
      setWithoutBackground(false);
      setPreserveOriginal(false);

      successNotification(t("image_deleted_successfully"));
    } catch (err) {
      console.error("Failed to delete image:", err);
      errorNotification(t("failed_to_delete_image"));
    } finally {
      setImageLoading(false);
    }
  };

  const handleRemoveNewImage = async (restoreCurrentImage = true) => {
    const currentImage = restoreCurrentImage ? details?.image : undefined;

    setValue("imageId", currentImage?.id ?? undefined, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
    await trigger("imageId");
    setNewImage(null);
    setNewImageFile(undefined);
    setOriginalImageFile(undefined);
    setImageChanged(false);
    setWithoutBackground(Boolean(currentImage?.without_background));
    setPreserveOriginal(false);
  };

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const onSubmit = async (data: any) => {
    if (hasPendingImageUpload(imageChanged, newImage)) {
      errorNotification(t("pending_image_upload_message"));
      return;
    }

    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        details
          ? await EndPoints.bannerTrademark.updateBanner(details?.id, data)
          : await EndPoints.bannerTrademark.createBanner(data);
        navigate(-1);
      },
      fallback: (error) => { },
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  const watchImageId = useWatch({ control, name: "imageId" });
  useEffect(() => {
    if (newImageFile && details?.image?.id !== watchImageId) {
      setImageChanged(true);
    }
  }, [watchImageId, details?.image?.id, newImageFile]);

  useEffect(() => {
    setNewImage(null);
    setNewImageFile(undefined);
    setOriginalImageFile(undefined);
    setImageChanged(false);
    setImageLoading(false);
    setPreserveOriginal(false);

    reset({
      description: getTranslations(details?.translations?.description) ?? null,
      text: getTranslations(details?.translations?.text),
      imageId: details?.image?.id,
      link: details?.link,
      rateableId: details?.rateableId,
      rateableType: details?.rateableType,
      for: 1,
      status: details?.status,
      trademarkId: details?.trademark?.id,
      categoryId: details?.category?.id
    } as IUpdateBanner);

    setWithoutBackground(details?.image?.without_background ?? false);

    if (details?.rateableType === 4) {
      setSelectedEntityType('store');
    } else if (details?.rateableType === 8) {
      setSelectedEntityType('trademark');
    }
  }, [details, reset]);

  useEffect(() => {
    const getCategories = async () => {
      if (!selectedEntityType || !selectedEntityId) {
        setCategories([]);
        setLoadingCategories(false);
        return;
      }
      try {
        const filters = selectedEntityType === 'store'
          ? [{ name: "store", value: selectedEntityId, operation: "eq" }]
          : [{ name: "trademark", value: selectedEntityId, operation: "eq" }];
        const { data } = await EndPoints.category.getAllCategories({
          light: true,
          perPage: -1,
          filters
        });
        setCategories(data.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoadingCategories(false);
      }
    };
    getCategories();
  }, [direction, selectedEntityId, selectedEntityType]);

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className={styles.title}>
            {details ? t("update_banner") : t("add_banner")}
          </span>
          <Divider className={styles.divider} />
          {loading.includes("details") ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <div className={styles.children}>
              <Row gutter={[8, 8]}>
                {languages?.map((lang, index) => {
                  return (
                    <Col span={12}>
                      <FieldBuilder
                        key={index}
                        name={`text[${lang?.code}]`}
                        label={`${t("text")} (${lang?.name})`}
                        input={{ type: "text" }}
                        rules={{ required: true }}
                      />
                    </Col>
                  );
                })}
                <Col span={12}>
                  <FieldBuilder
                    name="trademarkId"
                    label={`${t("trademark")}`}
                    width="large"
                    input={{
                      type: "select",
                      loading: trademarksLoading,
                      allowSearch: true,
                      options: trademarks?.map((store, index) => {
                        return {
                          key: index,
                          label: store.name,
                          value: store.id,
                        };
                      }),
                    }}
                  />
                </Col>
                <Col span={12}>
                  <FormItem label={t("rateable_type")} required>
                    <Controller
                      name="rateableType"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { ...field } }) => {
                        return (
                          <Select
                            {...field}
                            onSelect={() => {
                              setValue("rateableId", null);
                            }}
                            style={{ width: "100%" }}
                            placeholder={t("rateable_type")}
                          >
                            {banners?.map((inc, index) => {
                              return (
                                <Option key={index} value={inc}>
                                  {t(getBannerType(inc))}
                                </Option>
                              );
                            })}
                          </Select>
                        );
                      }}
                    />
                  </FormItem>
                </Col>
                {!rateableType && (
                  <Col span={12}>
                    <FieldBuilder
                      name=""
                      label={t("choice_rateable_type")}
                      input={{ type: "text" }}
                      disabled
                      rules={{ required: false }}
                    />
                  </Col>
                )}
                {rateableType === 1 && (
                  <Col span={12}>
                    <FieldBuilder
                      name="rateableId"
                      label={`${t("rateableId")}`}
                      width="large"
                      input={{
                        type: "select-pagination",
                        handleScroll: handleScroll,
                        allowSearch: true,
                        loading: productsLoading,
                        options: products.map((product, index) => {
                          return {
                            label: product.name,
                            value: product.id,
                          };
                        }),
                      }}
                    />
                  </Col>
                )}
                {rateableType === 2 && (
                  <Col span={12}>
                    <FieldBuilder
                      name="rateableId"
                      label={t("categories")}
                      width="large"
                      input={{
                        type: "select",
                        allowSearch: true,
                        loading: false,
                        options: allCategories
                          ?.filter(
                            (cat) => cat.grand === null && cat.parent === null
                          )
                          ?.map((tr, index) => {
                            return {
                              label: tr.name,
                              value: tr.id,
                            };
                          }),
                      }}
                    />
                  </Col>
                )}
                {rateableType === 3 && (
                  <Col span={12}>
                    <FieldBuilder
                      name="rateableId"
                      label={t("parent_category")}
                      width="large"
                      input={{
                        type: "select",
                        allowSearch: true,
                        loading: false,
                        options: allCategories
                          ?.filter(
                            (cat) => cat.grand !== null && cat.parent === null
                          )
                          ?.map((tr, index) => {
                            return {
                              label: tr.name,
                              value: tr.id,
                            };
                          }),
                      }}
                    />
                  </Col>
                )}
                {rateableType === 7 && (
                  <Col span={12}>
                    <FieldBuilder
                      name="rateableId"
                      label={t("child_category")}
                      width="large"
                      input={{
                        type: "select",
                        allowSearch: true,
                        loading: false,
                        options: allCategories
                          ?.filter(
                            (cat) => cat.grand !== null && cat.parent !== null
                          )
                          ?.map((tr, index) => {
                            return {
                              label: tr.name,
                              value: tr.id,
                            };
                          }),
                      }}
                    />
                  </Col>
                )}
                {rateableType === 4 && (
                  <>
                    <Col span={12}>
                      <FormItem label={t("store")} required>
                        <Controller
                          name="rateableId"
                          control={control}
                          rules={{ required: true }}
                          render={({ field: { ...field } }) => (
                            <Select
                              {...field}
                              showSearch
                              style={{ width: "100%" }}
                              loading={storeLoading}
                              placeholder={t("store")}
                              filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                              }
                              allowClear
                              options={stores.map((tr) => ({
                                label: tr?.name as string,
                                value: tr?.id as number,
                              }))}
                              onChange={(value) => {
                                field.onChange(value);
                                setSelectedEntityType('store');
                                setValue('categoryId', null);
                              }}
                            />
                          )}
                        />
                      </FormItem>
                    </Col>
                    {selectedEntityId && (
                      <Col span={12}>
                        <FieldBuilder
                          name="categoryId"
                          label={t("category")}
                          width="large"
                          input={{
                            type: "select",
                            allowSearch: true,
                            loading: loadingCategories,
                            options: categories?.map((category) => ({
                              label: category.name,
                              value: category.id,
                            })),
                          }}
                        />
                      </Col>
                    )}
                  </>
                )}
                {rateableType === 8 && (
                  <>
                    <Col span={12}>
                      <FormItem label={t("trademark")} required>
                        <Controller
                          name="rateableId"
                          control={control}
                          rules={{ required: true }}
                          render={({ field: { ...field } }) => (
                            <Select
                              {...field}
                              showSearch
                              style={{ width: "100%" }}
                              loading={trademarksLoading}
                              placeholder={t("trademark")}
                              filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                              }
                              allowClear
                              options={trademarks.map((tr) => ({
                                label: tr?.name as string,
                                value: tr?.id as number,
                              }))}
                              onChange={(value) => {
                                field.onChange(value);
                                setSelectedEntityType('trademark');
                                setValue('categoryId', null);
                              }}
                            />
                          )}
                        />
                      </FormItem>
                    </Col>
                    {selectedEntityId && (
                      <Col span={12}>
                        <FieldBuilder
                          name="categoryId"
                          label={t("category")}
                          width="large"
                          input={{
                            type: "select",
                            allowSearch: true,
                            loading: loadingCategories,
                            options: categories?.map((category) => ({
                              label: category.name,
                              value: category.id,
                            })),
                          }}
                        />
                      </Col>
                    )}
                  </>
                )}
                {rateableType === 5 && (
                  <Col span={12}>
                    <FieldBuilder
                      name="link"
                      label={t("link")}
                      input={{ type: "text" }}
                      rules={{ required: false }}
                    />
                  </Col>
                )}
                {rateableType === 6 && (
                  <Col span={12}>
                    <FieldBuilder
                      name="not_related"
                      disabled
                      label={t("not_related")}
                      input={{ type: "text" }}
                      rules={{ required: false }}
                    />
                  </Col>
                )}
                <Col span={12}>
                  <FormItem label={t("status")}>
                    <Controller
                      name="status"
                      control={control}
                      rules={{ required: false }}
                      render={({ field: { ...field } }) => {
                        return (
                          <Select
                            loading={false}
                            {...field}
                            style={{ width: "100%" }}
                            allowClear
                            placeholder={t("status")}
                          >
                            {[1, 2]?.map((tr, index) => {
                              return (
                                <Option key={index} value={tr}>
                                  {t(getUserStatus(tr))}
                                </Option>
                              );
                            })}
                          </Select>
                        );
                      }}
                    />
                  </FormItem>
                </Col>
                {languages?.map((lang, index) => {
                  return (
                    <Col span={12}>
                      <FieldBuilder
                        key={index}
                        name={`description[${lang?.code}]`}
                        label={`${t("description")} (${lang?.name})`}
                        input={{ type: "text-area" }}
                        rules={{ required: false }}
                      />
                    </Col>
                  );
                })}

                <ImageFieldBlock
                  fieldName="imageId"
                  currentId={details?.image?.id}
                  currentImage={details?.image}
                  newImageUrl={newImage}
                  isChanged={imageChanged}
                  onUpload={handleUploadNewImage}
                  onImageChange={handleImageChange}
                  onRemoveNewImage={handleRemoveNewImage}
                  setWithoutBackgrounds={setWithoutBackground}
                  preserveOriginal={preserveOriginal}
                  setPreserveOriginal={setPreserveOriginal}
                  onDeleteImage={handleDeleteImage}
                  isImageLoading={imageLoading}
                  value={withoutBackground}
                />
              </Row>
            </div>
          )}
        </div>
        <div>
          <Divider className={styles.divider} />
          <div className={styles.formFooter}>
            <Button
              onClick={() => {
                navigate(-1);
              }}
            >
              {t("cancel")}
            </Button>
            <Button loading={loadingSubmit} htmlType="submit" type="primary">
              {t("save")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const BannerTrademarkForm = () => {
  return (
    <BannerTrademarkContextProvider>
      <Form />
    </BannerTrademarkContextProvider>
  );
};

export default BannerTrademarkForm;
